import gql from "graphql-tag";
import { defineMessages } from "react-intl";
import { storage } from "cci-utility";
import { FORM_ERROR } from "final-form";
import { setConnectionInfo, storeCsrfToken } from "../graphql";

const messages = defineMessages({
  badCredentialsException: {
    id: "badCredentialsException",
    defaultMessage: "Invalid login credentials, please try again."
  },

  disabledException: {
    id: "disabledException",
    defaultMessage:
      "Your account is inactive, please contact the administrator."
  },

  credentialsExpiredException: {
    id: "credentialsExpiredException",
    defaultMessage:
      "Your password has expired, please contact the administrator."
  },

  lockedException: {
    id: "lockedException",
    defaultMessage:
      "Your account has been blocked due to too many failed attempts, please try again after 30 minutes."
  },

  unknownError: {
    id: "unknownError",
    defaultMessage: "An internal error has occurred, please try again later."
  },

  tokenExpired: {
    id: "tokenExpired",
    defaultMessage:
      "The forgot password link you used has expired, please try again."
  },

  errorResetPassword: {
    id: "errorResetPassword",
    defaultMessage: `There was a problem resetting your password, please contact support. Error: {message}`
  },

  passwordChanged: {
    id: "passwordChanged",
    defaultMessage: "Your password was changed successfully!"
  },

  noConnectionAvailable: {
    id: "noConnectionAvailable",
    defaultMessage:
      "Login failed because either internet connection or server is not available."
  },

  codeNotSentNoPhone: {
    id: "codeNotSentNoPhone",
    defaultMessage: "Please contact your supervisor to finish setting up your account."
  },

  codeSent: {
    id: "codeSent",
    defaultMessage: "A one time passcode has been sent to your phone."
  }
});

// TODO These mutations should be cleaned up to use a Promise in apollo-link-state to resolve and reject our async functions. We are currently using the context which is not very clean.

/**
 * Client side mutation that internally calls the login endpoint without GraphQL to authenticate the user.
 */
const loginMutation = gql`
  mutation login($userId: String, $password: String, $rememberMe: Boolean, $code: String) {
    login(userId: $userId, password: $password, rememberMe: $rememberMe, code: $code) @client
  }
`;

/**
 * Authenticates the specified user id and password with the server.
 *
 * If the user is authenticated then the indicator in the store is turned on.
 */
const login = (
  _,
  { userId, password, rememberMe, code },
  { cache, callback, showMessage }
) => {
  const request = new Request(`${cache.appUrl}/login`, {
    method: "POST",
    mode: "cors",
    body: `userId=${userId}&password=${password}&remember-me=${rememberMe}&code=${code}`,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded"
    },
    // Needed to save session cookie
    credentials: "include"
  });

  // Send the request to the server
  fetch(request)
    .then(response => {
      response.json().then(data => {
        if (response.ok) {
          // Authentication was successful

          storeCsrfToken(data.token);

          // User is now authenticated
          setConnectionInfo({
            authenticated: true
          });
        } else {
          // Authentication failed

          // Show the user the error message, used to avoid re-rendering in Final Form because using the callback with Toast causes an error
          if (showMessage) {
            showMessage(data);
          }

          // Perform the callback for setting the whole form error in Final Form
          if (callback) {
            callback({ [FORM_ERROR]: data });
          }
        }
      });
    })
    .catch(() => {
      if (showMessage) {
        showMessage({
          success: false,
          ...messages.noConnectionAvailable
        });
      }
    });

  return null;
};

/**
 * Client side mutation that internally calls the logout endpoint without GraphQL to authenticate the user.
 */
const logoutMutation = gql`
  mutation logout {
    logout @client
  }
`;

/**
 * Sends the logout request to the server.
 * Needed because we might need to call it twice (when CSRF token expires).
 */
const doLogout = (client, cache, token) => {
  const request = new Request(`${cache.appUrl}/logout`, {
    method: "POST",
    headers: {
      "X-CSRF-TOKEN": token
    },
    // Needed to delete session cookie
    credentials: "include"
  });

  // Send the request to the server
  fetch(request).then(
    response => {
      if (response.ok) {
        response.json().then(
          // Server replied with a new token so we have to resend our request. This happens if the user leaves the window open, session expires and then presses logout
          data => doLogout(client, cache, data.token),

          // There is no new token so we were successfully logged out
          () => client.resetStore()
        );
      } else {
        client.resetStore();
      }
    },

    // Error happens when the session expired, so we don't need to retry we simply reset the Apollo cache
    () => client.resetStore()
  );
};

/**
 * Logs the user out of their session with the server.
 */
const logout = (_, __, { cache, client }) => {
  storage
    .load({
      key: "s",
      autoSync: false,
      syncInBackground: false,
      defaultExpires: null
    })
    .then(
      token => {
        // Token loaded from local storage
        doLogout(client, cache, token);
      },

      // There is no local token so we simply reset the Apollo cache
      () => client.resetStore()
    );

  return null;
};

/**
 * Client side mutation that internally calls the server without GraphQL as the user is not authenticated.
 */
const forgotPasswordMutation = gql`
  mutation forgotPassword($userId: String) {
    forgotPassword(userId: $userId) @client
  }
`;

/**
 * Sends the forgot password request to the server and shows any applicable message to the user via
 * the showMessage function in the Apollo context.
 */
const forgotPassword = (_, { userId }, { cache, showMessage }) => {
  // Prepare the request to the server
  const request = new Request(`${cache.appUrl}/forgotPassword`, {
    method: "POST",
    mode: "cors",
    body: `emailAddress=${userId}`,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded"
    },
    credentials: "include"
  });

  // Send the request to the server and process the request
  fetch(request).then(response => {
    if (response.ok) {
      // Server either gave us an error message or said everything is ok, either way show a message
      response.json().then(showMessage);
    } else {
      // Authentication failed, show the user the error message
      response.json().then(() => showMessage(messages.unknownError));
    }
  });

  return null;
};

/**
 * Client side mutation that internally calls the server without GraphQL as the user is not authenticated.
 */
const resetPasswordMutation = gql`
  mutation resetPassword($token: String, $password: String) {
    resetPassword(token: $token, password: $password) @client
  }
`;

/**
 * Asks the server to reset the password using their "forgot password" token that they should have
 * received via e-mail.
 */
const resetPassword = (
  _,
  { token, password },
  { cache, routeToLogin, showMessage }
) => {
  // Prepare the request to the server
  const request = new Request(`${cache.appUrl}/resetPassword`, {
    method: "POST",
    mode: "cors",
    body: `token=${token}&password=${password}`,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded"
    },
    credentials: "include"
  });

  // Send the request to the server and process the request
  fetch(request).then(response => {
    if (response.ok) {
      // Server either gave us an error message or said everything is ok, either way show a message
      response.json().then(
        data => {
          showMessage(data);

          if (data.success === true) {
            /**
             * Use the context to redirect the user.
             *
             * Not the cleanest, but we do this because we have to redirect the user and that re-renders the page.
             * If we do both at the same time we get an error.
             */
            setTimeout(() => {
              if (routeToLogin) {
                routeToLogin();
              }
            }, 750);
          }
        },
        error => showMessage(error)
      );
    } else {
      // Authentication failed, show the user the error message
      response.json().then(() => showMessage(messages.unknownError));
    }
  });

  return null;
};

/**
 * Client side mutation that internally calls the server without GraphQL as the user is not authenticated.
 */
const isMfaEnabledMutation = gql`
  mutation isMfaEnabled($email: String) {
    isMfaEnabled(email: $email) @client
  }
`;

/**
 * Sends the isMfaEnabled request to the server, runs the setCode callback and 
 * shows any applicable message to the user via the showMessage function in the 
 * Apollo context.
 */
const isMfaEnabled = (_, { email }, { cache, setCode, showMessage, setResendAvailable }) => {
  // Prepare the request to the server
  const request = new Request(`${cache.appUrl}/isMfaEnabled`, {
    method: "POST",
    mode: "cors",
    body: `email=${email}`,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded"
    },
    credentials: "include"
  });

  // Send the request to the server and process the request
  fetch(request).then(response => {
    if (response.ok) {
      // Server either gave us an error message or said everything is ok, either way show a message
      response.json().then((res) => {
        setCode(res.success);

        if (res.id !== "mfaDisabled") {
          showMessage(res)
        }
        if (res.success) {
          setResendAvailable(false)
        }
      });
    } else {
      // Authentication failed, show the user the error message
      response.json().then(() => showMessage(messages.unknownError));
    }
  }).catch(e => {
    console.log(e)
    showMessage(messages.unknownError)
  });

  return null;
};

export {
  loginMutation,
  login,
  logoutMutation,
  logout,
  forgotPasswordMutation,
  forgotPassword,
  resetPasswordMutation,
  resetPassword,
  isMfaEnabledMutation,
  isMfaEnabled
};
